import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { GradientHoverCard, GRADIENT, gradientBorderVariants } from '@/components/common/GradientHoverCard'
import type { CompanyMemberDocument } from '@/types'

const roleLabels: Record<string, string> = {
  choreographer: 'Choreographer',
  dancer: 'Dancer',
  videographer: 'Videographer',
  photographer: 'Photographer',
  'guest-artist': 'Guest Artist',
  intern: 'Intern',
}

// ─── components ─────────────────────────────────────────────────────────────

interface MemberCardProps {
  member: CompanyMemberDocument & { id: string }
}

export function MemberCardSkeleton() {
  return (
    <div className="gradient-brand-border flex flex-col items-center gap-4 rounded-xl p-6 text-center">
      <Skeleton className="h-36 w-36 rounded-full" />
      <div className="w-full space-y-2">
        <Skeleton className="mx-auto h-4 w-3/4" />
        <div className="flex flex-wrap justify-center gap-1">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      </div>
    </div>
  )
}

export function MemberCard({ member }: MemberCardProps) {
  const avatar = (
    <Avatar className="h-36 w-36">
      <AvatarImage
        src={member.squarePhotoUrl ?? member.photoUrl}
        alt={member.name}
        className="animate-in fade-in duration-700 ease-out"
      />
      <AvatarFallback className="text-2xl">
        {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
      </AvatarFallback>
    </Avatar>
  )

  return (
    <GradientHoverCard>
      <Link
        to={`/company/${member.slug}`}
        className="group flex flex-col items-center gap-4 rounded-xl p-6 text-center"
      >
        {/* Avatar with brand gradient ring — synced to the card border opacity */}
        {member.photoCredit ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative rounded-full p-[2px]">
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{ background: GRADIENT }}
                  variants={gradientBorderVariants}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
                <div className="relative rounded-full p-[2px] bg-background">{avatar}</div>
              </div>
            </TooltipTrigger>
            <TooltipContent>{member.photoCredit}</TooltipContent>
          </Tooltip>
        ) : (
          <div className="relative rounded-full p-[2px]">
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{ background: GRADIENT }}
              variants={gradientBorderVariants}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
            <div className="relative rounded-full p-[2px] bg-background">{avatar}</div>
          </div>
        )}

        <div className="space-y-2">
          <h3 className="font-semibold leading-tight transition-colors group-hover:text-primary">
            {member.name}
          </h3>
          <div className="flex flex-wrap justify-center gap-1">
            {member.roles.map((role) => (
              <Badge key={role} variant="secondary" className="text-xs transition-colors group-hover:bg-brand-white group-hover:text-brand-black">
                {roleLabels[role] ?? role}
              </Badge>
            ))}
          </div>
          {!member.isActive && (
            <Badge variant="outline" className="text-xs text-muted-foreground">Alumni</Badge>
          )}
        </div>
      </Link>
    </GradientHoverCard>
  )
}
